/**
 * Large UPS descriptor-engine save bridge (#997 Story 6.5) — the Large UPS
 * instantiation of the generic `useRepeatableEngineSaveBridge` (mirror of
 * `useSmallUpsEngineSaveBridge`).
 *
 * Replaces the parent-owned `useLargeUpsEditSession` for the persisted (Request
 * Detail) path once the Large UPS step runs on `DescriptorEngineSection`.
 * Supplies the LARGE UPS bindings (query hook, flush, canonical content-key
 * comparator, invalidation keys, legacy synthesis, editedData key) and renames
 * the generic handle to the unchanged bespoke `LargeUpsConfigurationsSaveHandle`
 * contract so every existing call site keeps working. The flush's created-id map
 * is surfaced through `flushPendingLargeUpsChanges` for the Obs 6 staged-upload
 * remap.
 *
 * See `useRepeatableEngineSaveBridge` for the held-baseline / same-chain /
 * reset-to-flush-result semantics (the 6-3 review-patch cluster).
 */

import { useMemo } from 'react';

import {
  largeUpsConfigurationsKeys,
  useLargeUpsConfigurations,
} from './queries/useLargeUpsConfigurations';
import {
  useRepeatableEngineSaveBridge,
  type AggregateSaveBridgeExtras,
} from './useRepeatableEngineSaveBridge';
import { buildLegacyLargeUpsFromRequestFields } from '../components/largeups/largeUpsListUtils';
import {
  flushLargeUpsEditSession,
  largeUpsEditSessionHasChanges,
} from '../services/largeUpsEditSessionFlush';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { LargeUpsConfigurationsSaveHandle } from '../request/steps/largeups/subsections/largeUpsConfigurationsSaveHandle';
import type { LargeUpsConfiguration } from '../types/largeUps';

export type UseLargeUpsEngineSaveBridgeParams = {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy unit synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /** The request payload's PERSISTED `largeUpsConfigurations` embed. */
  persistedEmbedLargeUps?: unknown;
  /** Live session list — `editedData.largeUpsConfigurations` (engine bridge writes it). */
  editedLargeUpsConfigurations?: unknown;
  /** True when Large UPS was touched this session (`dirtyFields.has('largeUpsConfigurations')`). */
  largeUpsConfigurationsDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: LargeUpsConfiguration[] | null;
  onDirtyChange?: (dirty: boolean) => void;
};

export function useLargeUpsEngineSaveBridge({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedLargeUps,
  editedLargeUpsConfigurations,
  largeUpsConfigurationsDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
}: UseLargeUpsEngineSaveBridgeParams): {
  saveHandle:
    | (LargeUpsConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: LargeUpsConfiguration[] | null;
} {
  const {
    saveHandle: genericHandle,
    registerInstanceSaveHandle,
    sessionBaseline,
  } = useRepeatableEngineSaveBridge<LargeUpsConfiguration>({
    requestId,
    isEditing,
    isOfflineRequest,
    legacyRequestFields,
    persistedEmbedInstances: persistedEmbedLargeUps,
    editedInstances: editedLargeUpsConfigurations,
    instancesDirty: largeUpsConfigurationsDirty,
    applyEditedDataPatch,
    restoredBaseline,
    onDirtyChange,
    editedDataKey: 'largeUpsConfigurations',
    useInstancesQuery: useLargeUpsConfigurations,
    hasChanges: largeUpsEditSessionHasChanges,
    flush: flushLargeUpsEditSession,
    invalidateListKeys: id => [
      largeUpsConfigurationsKeys.list(id),
      largeUpsConfigurationsKeys.grouped(id),
    ],
    buildLegacyFromRequestFields: buildLegacyLargeUpsFromRequestFields,
  });

  const saveHandle = useMemo(():
    | (LargeUpsConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null => {
    if (!genericHandle) {
      return null;
    }
    return {
      isLargeUpsEditorOpen: genericHandle.isEditorOpen,
      hasUnsavedLargeUpsEditor: genericHandle.hasUnsavedEditor,
      commitOpenLargeUpsEditor: genericHandle.commitOpenEditor,
      saveUnsavedLargeUpsIfNeeded: genericHandle.saveUnsavedIfNeeded,
      hasPendingLargeUpsChanges: genericHandle.hasPendingChanges,
      flushPendingLargeUpsChanges: genericHandle.flushPendingChanges,
      tryDismissLargeUpsEditor: genericHandle.tryDismissEditor,
      discardOpenLargeUpsEditor: genericHandle.discardOpenEditor,
      // #1501 aggregate save seams (uniform names across all six families)
      getPendingDiff: genericHandle.getPendingDiff,
      absorbExternalFlushResult: genericHandle.absorbExternalFlushResult,
      remapSessionIds: genericHandle.remapSessionIds,
      resetSessionAfterScopeRemoval:
        genericHandle.resetSessionAfterScopeRemoval,
    };
  }, [genericHandle]);

  return { saveHandle, registerInstanceSaveHandle, sessionBaseline };
}
